/**
 * Marketing / brochure routes that must not bounce anonymous visitors to /login.
 *
 * Used by Edge middleware AND the client AuthProvider. Keep them identical —
 * a mismatch is how ad clicks land on the login page after a 200 HTML response.
 */

export const PUBLIC_PATHS = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/reset',
  '/verify-email',
  '/centres',
  '/home',
  '/payments',
  '/payment-success',
  '/subscription-expired',
  '/campaign',
  '/try',
  '/operator/login',
  // School admins and teachers sign in here with Firebase, not NextAuth — see
  // the /school and /teacher exemption in middleware.ts.
  '/school/login',
  // First-login teachers land here before AuthProvider has restored the
  // Firebase user. If this is private, the unauthenticated guard fires in
  // that gap and skips them past the form, back to login.
  '/school/change-password',
  '/home/learning-center-registration',
  '/test-viz',
  '/test-viz-advanced',
  '/test-images',
  '/assessment',
  '/ai-skills',
  '/professional/register',
  '/professional/login',
  '/professional/certificate/verify',
  '/weekly-certificates',
  '/terms',
  '/privacy',
  '/states',
  '/earn',
];

/** Country-prefixed brochure pages. Named slugs — not /[cc]/* — so a future
 *  authenticated route under a country prefix stays protected. */
export const DYNAMIC_PUBLIC_PATH_PATTERNS = [
  /^\/[a-z]{2}\/(home)(?:\/.*)?$/i,
  /^\/[a-z]{2}\/enroll(?:\/.*)?$/i,
  /^\/[a-z]{2}\/(active-oversight|about|compare|contact|curriculum|essentials|faq|features|full-service|homeschool|host|how-it-works|microschool|products|projects|refer)(?:\/.*)?$/i,
];

/**
 * Short URLs ads and share links actually use. Middleware 307s these before
 * the auth gate, otherwise `/us` (no /home) dumps the visitor on /login.
 */
export const PUBLIC_REDIRECTS: Record<string, string> = {
  '/us': '/us/home',
  '/ng': '/ng/home',
  '/gb': '/gb/home',
  '/uk': '/gb/home',
  '/us/refer': '/home/refer-and-earn',
  '/us/about': '/home/about',
  '/us/contact': '/home/contact',
};

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function publicRedirectTarget(pathname: string): string | null {
  const path = normalizePathname(pathname).toLowerCase();
  return PUBLIC_REDIRECTS[path] || null;
}

/**
 * Where to send a signed-out visitor from a protected page. School admins and
 * teachers have their own sign-in page; everyone else uses /login.
 *
 * Matched by whole segment, like the /school and /teacher exemption in
 * middleware.ts, so the older /teacher-dashboard route is not swept in.
 */
export function signInPathFor(pathname: string): string {
  const path = normalizePathname(pathname);
  const inSchoolArea = ['/school', '/teacher'].some((p) => path === p || path.startsWith(`${p}/`));
  return inSchoolArea ? '/school/login' : '/login';
}

export function isPublicPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'))) {
    return true;
  }
  return DYNAMIC_PUBLIC_PATH_PATTERNS.some(pattern => pattern.test(path));
}
